import {
  IssueAssembleResponse, Project, ProjectRole
} from '../models/issue';
import { usePromise } from '../utils/usePromise';
import { useVuex } from '../utils/useVuex';
import { computed, Ref, watch, ref } from '@vue/composition-api';

import { getProjectRoles } from '../api/issue';
import { ActionStore } from './action';
import { ShareStore } from './share';

export const useProject = (
  actionStore: ActionStore,
  issueAssemble: Ref<IssueAssembleResponse | null>,
  { isVisitor, shareProjectId, shareInfo }: ShareStore
) => {
  const vuex = useVuex();

  const projectRoleLoading = ref<boolean>(true);

  const currentProjectId = computed(() => {
    if (
      actionStore.action.value?.type === 'Create' ||
      actionStore.action.value?.type === 'PreviewType'
    ) {
      return actionStore.action.value.projectId;
    } if (actionStore.action.value?.type === 'Edit') {
      return issueAssemble.value?.issueVO?.projectId;
    }
    return undefined;
  });

  const projectList = computed<Project[]>(() =>
    isVisitor.value
      ? [
        {
          id: shareProjectId.value!,
          pkey: shareInfo.value.pkey,
          pname: shareInfo.value.projectName,
          backgroundColor: '#FF8384'
        }
      ]
      : vuex.state.app.projectList
  );

  const currentProject = computed(() =>
    projectList.value.find((p) => p.id === currentProjectId.value)
  );

  const { result: projectRoles, refresh: refreshRoles } =
    usePromise(async () => {
      if (
        currentProjectId.value &&
        !vuex.getters.allRoles.some(
          (r: ProjectRole) => r.roleCode === 'anonymous'
        )
      ) {
        return await getProjectRoles(currentProjectId.value);
      }
      return [];
    }, []);

  watch(currentProjectId, () => {
    projectRoleLoading.value = true;
    refreshRoles().then(() => {
      projectRoleLoading.value = false;
    });
  }, { immediate: true });

  return {
    projectList,
    currentProjectId,
    currentProject,
    projectRoles,
    projectRoleLoading
  };
};

export type ProjectStore = ReturnType<typeof useProject>;
